import { useCallback, useEffect, useState } from "react";
import { FileDown, Search, ChevronDown, ChevronRight, Mail, Loader2, Check, Ruler } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { api, errMsg, downloadBlob, API } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ColorSwatch } from "@/components/ColorSwatch";
import { sortSizeEntries } from "@/lib/sizes";
import { ProductionFiles } from "@/components/orders/ProductionFiles";
import ProtectedImg from "@/components/b2b/ProtectedImg";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const QUEUE_STATUSES = ["Wysłane do produkcji"];
const PROD_STATUSES = ["Wysłane do produkcji", "Zrealizowane", "Wysłane Garwolin", "Wysłane Nowy Sącz", "Wysłane do klienta"];
const DONE_STATUSES = ["Zrealizowane", "Wysłane Garwolin", "Wysłane Nowy Sącz", "Wysłane do klienta"];

const iso = (d) => d.toISOString().slice(0, 10);
const datePresets = () => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const q = Math.floor(m / 3);
  return {
    month: { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) },
    quarter: { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) },
    year: { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)) },
  };
};

const sizeStats = (o) => {
  let total = 0, done = 0;
  o.items.filter((it) => !it.ref).forEach((it) => Object.values(it.size_done || {}).forEach((d) => { total++; if (d) done++; }));
  return { total, done };
};

const RedfoxCard = ({ card }) => {
  const markings = card.markings || [];
  const viEntries = Object.entries(card.variant_index || {});
  return (
    <div data-testid="redfox-prod-card" className="mb-2.5 rounded-lg border border-red-500/40 bg-red-500/[0.06] px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-red-400">Karta produkcyjna · REDFOX</span>
        <span className="text-[10px] font-semibold text-muted-foreground">(ze snapshotu zamówienia — niezmienna)</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
        {(card.variant_name || card.print_color) && <div data-testid="redfox-card-variant"><span className="text-muted-foreground">Wariant / logowanie:</span> <span className="font-semibold">{card.variant_name || ""}{card.print_color ? ` (nadruk: ${card.print_color})` : ""}</span></div>}
        {card.base_sku && <div><span className="text-muted-foreground">Odzież bazowa:</span> <span className="font-semibold">{card.base_sku}{card.base_color ? ` / ${card.base_color}` : ""}</span></div>}
        {card.base_notes && <div><span className="text-muted-foreground">Uwagi do odzieży:</span> <span className="font-medium">{card.base_notes}</span></div>}
      </div>
      {card.production_notes && <div className="mb-2 text-xs"><span className="text-muted-foreground">Uwagi produkcyjne:</span> <span className="font-medium whitespace-pre-wrap">{card.production_notes}</span></div>}
      {viEntries.length > 0 && (
        <div className="mb-2" data-testid="redfox-variant-index">
          <p className="text-[11px] font-bold text-foreground mb-1">Indeksy wariantów (WAPRO)</p>
          <div className="flex flex-wrap gap-1.5">
            {viEntries.map(([k, v]) => { const [c, s] = k.split("|"); return (
              <span key={k} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-[11px] font-medium"><span className="text-muted-foreground">{c}/{s}</span><span className="font-mono font-bold">{v || "—"}</span></span>
            ); })}
          </div>
        </div>
      )}
      {card.no_branding && <p className="text-xs font-semibold text-muted-foreground">Produkt bez znakowania.</p>}
      <div className="space-y-2">
        {markings.map((m, i) => (
          <div key={m.id || i} data-testid={`redfox-marking-${i}`} className="rounded-md border border-border bg-background/60 px-2.5 py-2">
            <p className="text-xs font-bold text-foreground">Znakowanie {i + 1}: {m.place} <span className="font-semibold text-red-400">{m.technique}</span>{(m.width_cm || m.height_cm) ? <span className="font-normal text-muted-foreground"> · {m.width_cm || "?"}×{m.height_cm || "?"} cm ({m.dimension_type === "max" ? "maks." : "docelowy"})</span> : null}</p>
            <div className="mt-1 space-y-0.5 text-[11px]">
              {m.position_desc && <div><span className="text-muted-foreground">Położenie:</span> {m.position_desc}</div>}
              {m.graphic_desc && <div><span className="text-muted-foreground">Grafika:</span> {m.graphic_desc}</div>}
              {m.color && <div><span className="text-muted-foreground">Kolor:</span> {m.color}</div>}
              {m.notes && <div><span className="text-muted-foreground">Uwagi:</span> {m.notes}</div>}
              {(m.size_overrides || []).map((ov, j) => (
                <div key={j} className="text-muted-foreground">Wyjątek {ov.size}: {(ov.width_cm || ov.height_cm) ? `${ov.width_cm || "?"}×${ov.height_cm || "?"} cm` : ""} {ov.position_desc || ""}</div>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              {m.viz_file_id && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold text-muted-foreground">Wizualizacja</span>
                  <ProtectedImg fileId={m.viz_file_id} className="h-24 w-24 rounded object-contain border border-border bg-[#0D1117]" />
                </div>
              )}
              {m.production_file_id && (
                <Button type="button" data-testid={`redfox-prodfile-${i}`} variant="outline" size="sm" className="gap-2 font-semibold border-red-500/40 text-red-300 hover:bg-red-500/10"
                  onClick={() => downloadBlob(`/files/${m.production_file_id}`, `plik-produkcyjny-${i + 1}`, "pliku produkcyjnego")}>
                  <FileDown className="h-4 w-4" />Plik produkcyjny
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OrderCard = ({ o, onSize, onAllRequest, onUncheckAll, onStatus, onNotifyClient, confirmSize, onZoom, canStatus = true, pdfBase = "/production", vizMap = {}, isAdmin = false, onSentDate, isHaft = false, onHaftProject }) => {
  const total = o.items.filter((it) => !it.ref).reduce((a, it) => a + it.quantity, 0);
  const { total: totS, done: doneS } = sizeStats(o);
  let donePieces = 0;
  o.items.filter((it) => !it.ref).forEach((it) => Object.entries(it.sizes || {}).forEach(([s, q]) => { if (it.size_done && it.size_done[s]) donePieces += q; }));
  const pct = total ? Math.round((donePieces / total) * 100) : 0;
  const [cardOpen, setCardOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [pdfBusy, setPdfBusy] = useState(false);
  const confirm = useConfirm();
  const toggleItem = (idx) => setExpanded((prev) => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  const [measBusy, setMeasBusy] = useState(false);
  const measCid = o.customer?.id || o.customer_id;
  const uploadMeas = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    if (!list.length) return;
    if (!measCid) { toast.error("Brak powiązanego kontrahenta w zamówieniu"); return; }
    setMeasBusy(true);
    let ok = 0;
    for (const f of list) {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", "measurement");
      try { await api.post(`/customers/${measCid}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    if (ok) toast.success(`Dodano zdjęć pomiarowych: ${ok} → kartoteka ${o.customer.short_name || o.customer.name}`);
    setMeasBusy(false);
  };
  return (
    <div data-testid={`prod-order-${o.number}`} className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 border-b border-border bg-secondary/50 px-5 py-3.5">
        <div className="flex items-start gap-2.5 min-w-0">
          <button type="button" data-testid={`prod-card-toggle-${o.number}`} onClick={() => setCardOpen((v) => !v)}
            className="mt-0.5 shrink-0 rounded-md p-1 text-amber-300 hover:bg-accent/60 transition-colors" title={cardOpen ? "Zwiń kartę" : "Rozwiń kartę"}>
            {cardOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xs font-bold text-amber-300">{o.number}</span>
              <StatusBadge status={o.status} />
              {!isHaft && (
                <span data-testid={`prod-dtf-${o.number}`} title="Informacja o wydruku DTF (ustawia administrator)"
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold ${o.dtf_printed ? "bg-amber-400/15 text-amber-300" : "bg-secondary text-muted-foreground"}`}>
                  {o.dtf_printed ? <><Check className="h-3 w-3" />Wydruk DTF</> : "Wydruk DTF: nie"}
                </span>
              )}
              <span data-testid={`prod-files-flag-${o.number}`} title="Automatyczne — zaznaczone, gdy dołączono pliki produkcyjne"
                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold ${o.production_files?.length ? "bg-indigo-400/15 text-indigo-300" : "bg-secondary text-muted-foreground"}`}>
                {o.production_files?.length ? <><Check className="h-3 w-3" />Pliki produkcyjne ({o.production_files.length})</> : "Pliki produkcyjne: nie"}
              </span>
              {isHaft && (
                <label data-testid={`haft-project-${o.number}`} title="Oznacz, czy projekt haftu jest gotowy"
                  className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-0.5 text-[11px] font-bold cursor-pointer select-none">
                  <Checkbox checked={!!o.haft_project} onCheckedChange={(v) => onHaftProject && onHaftProject(o, !!v)} className="h-4 w-4" />
                  <span className={o.haft_project ? "text-amber-300" : "text-muted-foreground"}>Projekt</span>
                </label>
              )}
            </div>
            <p className="mt-1 font-display text-xl font-extrabold truncate" data-testid={`prod-customer-${o.number}`}>{o.customer.short_name || o.customer.name}</p>
            {o.customer.address && <p className="text-xs font-medium text-muted-foreground truncate">Odbiorca: {o.customer.address}</p>}
            {o.employee_name && <p className="text-xs font-medium text-muted-foreground truncate">Handlowiec: {o.employee_name}</p>}
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Data wysyłki na produkcję:</span>
              {isAdmin ? (
                <input type="date" data-testid={`prod-sentdate-${o.number}`} value={(o.sent_to_production_at || o.created_at || "").slice(0, 10)} onChange={(e) => onSentDate && onSentDate(o, e.target.value)} className="h-7 rounded border border-input bg-background px-2 text-[11px] font-semibold" title="Data wysyłki na produkcję — edycja tylko dla administratora" />
              ) : (
                <span className="text-xs font-bold text-foreground">{(o.sent_to_production_at || o.created_at) ? new Date(o.sent_to_production_at || o.created_at).toLocaleDateString("pl-PL") : "—"}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Sztuk</p>
            <p className="font-display text-2xl font-extrabold tabular-nums">{total}</p>
          </div>
          <div className="w-[190px]">
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Status</p>
            {canStatus ? (
            <Select value={o.status} onValueChange={(v) => v !== o.status && onStatus(o, v)}>
              <SelectTrigger data-testid={`prod-status-trigger-${o.number}`} className="h-9"><SelectValue placeholder={o.status} /></SelectTrigger>
              <SelectContent>
                {PROD_STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`prod-status-opt-${o.number}-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            ) : <div className="h-9 flex items-center"><StatusBadge status={o.status} /></div>}
          </div>
          <Button data-testid={`prod-pdf-${o.number}`} variant="outline" size="sm" disabled={pdfBusy} className="gap-2 font-semibold self-end h-9" onClick={async () => { const withViz = await confirm({ title: "Drukować wizualizacje?", description: "Podgląd wizualizacji jest zawsze na pierwszej stronie. Dołączyć też pełne wizualizacje/grafiki do wydruku? Zwykle nie są już potrzebne.", confirmText: "Tak, dołącz", cancelText: "Nie, bez nich" }); setPdfBusy(true); try { await downloadBlob(`${pdfBase}/orders/${o.id}/pdf?viz=${withViz ? 1 : 0}`, `produkcja-${o.number.replace(/\//g, "-")}.pdf`, "PDF"); } catch (e) {} finally { setPdfBusy(false); } }}>{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}{pdfBusy ? "Generowanie…" : "PDF"}</Button>
          {canStatus && ["Wysłane do klienta", "Wysłane Garwolin", "Wysłane Nowy Sącz", "Zrealizowane"].includes(o.status) && (
            <Button data-testid={`prod-notify-${o.number}`} variant="outline" size="sm" className="gap-2 font-semibold self-end h-9 border-sky-500/40 text-sky-400 hover:bg-sky-500/10" onClick={() => onNotifyClient(o)}><Mail className="h-4 w-4" />E-mail do klienta</Button>
          )}
        </div>
      </div>

      <div className="border-b border-border px-5 py-2.5" data-testid={`prod-progress-${o.number}`}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Postęp produkcji</span>
          <span className="font-mono text-xs font-semibold tabular-nums" data-testid={`prod-progress-label-${o.number}`}>{pct}% · {doneS}/{totS} rozm. · {donePieces}/{total} szt.</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {o.notes && (
        <div className="border-b border-border bg-amber-400/[0.06] px-5 py-2.5" data-testid={`prod-notes-${o.number}`}>
          <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400/90">Uwagi</span>
          <p className="text-sm font-medium text-foreground whitespace-pre-wrap">{o.notes}</p>
        </div>
      )}

      <div className="border-b border-border px-5 py-2.5">
        <label data-testid={`prod-meas-add-${o.number}`} className={`inline-flex items-center gap-2 rounded-lg border border-dashed border-sky-400/50 px-3 py-1.5 text-xs font-bold text-sky-300 transition-colors ${measBusy ? "opacity-60" : "cursor-pointer hover:bg-sky-400/10"}`} title="Dodaj zdjęcie pomiarowe do kartoteki kontrahenta">
          {measBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ruler className="h-4 w-4" />}
          Dodaj zdjęcie pomiarowe
          <input data-testid={`prod-meas-input-${o.number}`} type="file" accept="image/*,.heic,.heif" capture="environment" multiple className="hidden" disabled={measBusy}
            onChange={(e) => { const f = e.target.files; e.target.value = ""; uploadMeas(f); }} />
        </label>
      </div>

      {cardOpen && (<>
      <div className="flex items-center justify-between border-b border-border px-5 py-2.5">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <Checkbox data-testid={`prod-selectall-${o.number}`} className="h-5 w-5" checked={totS > 0 && doneS === totS}
            onCheckedChange={(v) => (v ? onAllRequest(o) : onUncheckAll(o))} />
          <span className="text-sm font-bold">Zaznacz wszystkie rozmiary jako wykonane</span>
        </label>
        <span className="font-mono text-xs font-semibold text-muted-foreground">{doneS}/{totS} rozmiarów</span>
      </div>

      <div className="divide-y divide-border">
        {o.items.map((it, idx) => {
          const isOpen = expanded.has(idx);
          const ref = it.ref;
          const itSizes = Object.keys(it.sizes || {});
          const itDone = itSizes.filter((s) => it.size_done && it.size_done[s]).length;
          const itComplete = itSizes.length > 0 && itDone === itSizes.length;
          return (
          <div key={idx} data-testid={`prod-item-${o.number}-${idx}`}>
            <button type="button" data-testid={`prod-item-toggle-${o.number}-${idx}`} onClick={() => toggleItem(idx)}
              className={`flex w-full items-center gap-3 px-5 py-3 text-left transition-colors ${ref ? "bg-muted/30 hover:bg-muted/50" : itComplete ? "bg-emerald-500/5 hover:bg-emerald-500/10" : "hover:bg-accent/40"}`}>
              {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className={`font-bold ${ref ? "text-muted-foreground" : ""}`}>{it.sku}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground">{it.name}</span>
              {ref
                ? <span data-testid={`prod-item-ref-${o.number}-${idx}`} className="inline-flex items-center gap-1 shrink-0 rounded-md bg-amber-400/15 px-2 py-0.5 text-[11px] font-bold text-amber-300">HAFT · realizuje Natalia</span>
                : <span data-testid={`prod-item-progress-${o.number}-${idx}`} className={`inline-flex items-center gap-1 shrink-0 rounded-md px-2 py-0.5 font-mono text-[11px] font-bold ${itComplete ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>{itComplete ? <><Check className="h-3 w-3" />Gotowe</> : `${itDone}/${itSizes.length}`}</span>}
              <span className="font-display text-lg font-extrabold tabular-nums text-amber-300 shrink-0">{it.quantity}<span className="ml-1 text-xs font-semibold text-muted-foreground">szt.</span></span>
            </button>
            {isOpen && (
            <div className="px-5 pb-3.5">
              <div className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold"><ColorSwatch name={it.color} size={12} />{it.color}</span>
                {it.branding?.type && it.branding.type !== "Same metki" && it.branding.placements?.length > 0 && (
                  <span className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold text-foreground">Nadruk: {it.branding.type} ({it.branding.placements.join(", ")})</span>
                )}
              </div>
              {it.notes && (
                <div data-testid={`prod-item-notes-${o.number}-${idx}`} className="mb-2.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs font-medium text-amber-200 whitespace-pre-wrap">Uwagi: {it.notes}</div>
              )}
              {it.viz_id && vizMap[it.viz_id] && (
                <div data-testid={`prod-item-viz-${o.number}-${idx}`} className="mb-2.5 flex items-center gap-2 rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1.5">
                  {(vizMap[it.viz_id].front_image_id || vizMap[it.viz_id].base_image_id) && <img src={`${API}/files/${vizMap[it.viz_id].front_image_id || vizMap[it.viz_id].base_image_id}`} alt="" className="h-10 w-10 rounded object-contain bg-[#0D1117]" />}
                  <span className="text-xs font-semibold text-sky-200">Wizualizacja: {vizMap[it.viz_id].title_text || vizMap[it.viz_id].title || vizMap[it.viz_id].product_name || "Wizualizacja"}</span>
                </div>
              )}
              {it.b2b_card && <RedfoxCard card={it.b2b_card} />}
              <div className="flex flex-col gap-1.5">
                {sortSizeEntries(it.sizes).map(([size, qty]) => {
                  const done = !!(it.size_done && it.size_done[size]);
                  return (
                    <label key={size} data-testid={`prod-size-${o.number}-${idx}-${size}`}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${ref ? "border-border opacity-70 cursor-default" : "cursor-pointer"} ${done && !ref ? "border-emerald-500/40 bg-emerald-500/5" : !ref ? "border-border hover:bg-accent/40" : ""}`}>
                      <Checkbox data-testid={`prod-size-check-${o.number}-${idx}-${size}`} className="h-5 w-5 shrink-0" checked={done} disabled={ref}
                        onCheckedChange={ref ? undefined : (v) => (v ? confirmSize(o, it.idx, size) : onSize(o, it.idx, size, false))} />
                      <span className={`font-mono text-sm font-bold w-16 ${done ? "line-through opacity-60" : ""}`}>{size}</span>
                      <span className={`flex-1 text-sm font-medium ${done ? "line-through opacity-60 text-muted-foreground" : ""}`}>{ref ? "Haft — realizuje Natalia" : `Rozmiar ${size}`}</span>
                      <span className="font-display text-lg font-extrabold tabular-nums text-amber-300 shrink-0">{qty}<span className="ml-1 text-xs font-semibold text-muted-foreground">szt.</span></span>
                    </label>
                  );
                })}
              </div>
            </div>
            )}
          </div>
          );
        })}
      </div>

      {(() => {
        const imgs = (o.files || []).filter((f) => f.content_type === "image/jpeg" || f.content_type === "image/png");
        if (!imgs.length) return null;
        return (
          <div className="border-t border-border px-5 py-3" data-testid={`prod-visuals-${o.number}`}>
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Wizualizacja</p>
            <div className="flex flex-wrap gap-2.5">
              {imgs.map((f) => (
                <button key={f.id} type="button" onClick={() => onZoom(`${API}/files/${f.id}`, f.filename)}
                  data-testid={`prod-visual-thumb-${o.number}-${f.id}`}
                  className="group relative block h-20 w-20 overflow-hidden rounded-lg border border-border bg-secondary transition-colors hover:border-amber-400 cursor-zoom-in"
                  title={`${f.filename} — kliknij, aby powiększyć`}>
                  <img src={`${API}/files/${f.id}`} alt={f.filename} loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {o.branding?.type && o.branding.type !== "Same metki" && (
        <div className="border-t border-border px-5 py-2.5 text-xs font-medium text-muted-foreground">Znakowanie: <span className="font-bold text-foreground">{o.branding.type}{o.branding.placements?.length ? ` (${o.branding.placements.join(", ")})` : ""}</span></div>
      )}
      <div className="border-t border-border px-5 py-3">
        <ProductionFiles order={o} canEdit />
      </div>
      </>)}
    </div>
  );
};

export const ProductionView = ({ base = "/production", canStatus = true, pdfBase = "/production", emptyText = "Brak zamówień do produkcji.", isAdmin = false }) => {
  const [orders, setOrders] = useState([]);
  const [vizMap, setVizMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [query, setQuery] = useState("");
  const [doneStatus, setDoneStatus] = useState("all");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`${base}/orders`); setOrders(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/visualizations").then((r) => { const m = {}; (r.data || []).forEach((v) => { m[v.id] = v; }); setVizMap(m); }).catch(() => {}); }, []);
  useEffect(() => { const id = setInterval(() => load(), 10000); return () => clearInterval(id); }, [load]);

  const apply = (o, data) => {
    if (data.status !== o.status) { load(); return; }
    setOrders((prev) => prev.map((x) => (x.id === o.id ? data : x)));
  };
  const onSize = async (o, idx, size, done) => {
    try { const { data } = await api.post(`${base}/orders/${o.id}/size`, { index: idx, size, done }); apply(o, data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onAll = async (o, done) => {
    try { const { data } = await api.post(`${base}/orders/${o.id}/all`, { done }); apply(o, data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const askConfirm = useConfirm();
  const onStatus = async (o, status) => {
    if (!(await askConfirm({ title: "Zmienić status zamówienia?", description: `Zmienić status zamówienia ${o.number} na „${status}”?`, confirmText: "Zmień status" }))) return;
    try { await api.patch(`/orders/${o.id}/status`, { status }); toast.success(`${o.number}: status → ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const onSentDate = async (o, date) => {
    if (!date) return;
    try { await api.patch(`/orders/${o.id}/sent-date`, { date }); toast.success(`${o.number}: data wysłania na produkcję zaktualizowana`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const onHaftProject = async (o, v) => {
    try { await api.patch(`/orders/${o.id}/haft-project`, { value: v }); toast.success(`${o.number}: projekt ${v ? "✓ gotowy" : "— cofnięto"}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const [backfillBusy, setBackfillBusy] = useState(false);
  const onBackfill = async () => {
    if (!(await askConfirm({ title: "Uzupełnić datę wysłania na produkcję?", description: "Dla starszych zamówień bez daty wysłania zostanie ona ustawiona na podstawie historii (data wysłania na produkcję lub data utworzenia). Operacja nie nadpisuje istniejących dat.", confirmText: "Uzupełnij daty" }))) return;
    setBackfillBusy(true);
    try { const { data } = await api.post(`/orders/backfill-sent-date`); toast.success(`Uzupełniono daty dla ${data.updated} zamówień`); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBackfillBusy(false); }
  };

  const onNotifyClient = async (o) => {
    if (!(await askConfirm({ title: "Wysłać e-mail do klienta?", description: `Wysłać do klienta powiadomienie o statusie „${o.status}” zamówienia ${o.number}? E-mail trafi na adres zapisany w kartotece klienta.`, confirmText: "Wyślij e-mail" }))) return;
    try { const { data } = await api.post(`/orders/${o.id}/notify-client`); toast.success(`Wysłano e-mail do klienta: ${data.sent_to}`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doConfirm = () => {
    const cf = confirm; setConfirm(null);
    if (!cf) return;
    if (cf.kind === "size") onSize(cf.order, cf.index, cf.size, true);
    else onAll(cf.order, true);
  };

  if (loading) return <div className="p-8 text-center font-medium text-muted-foreground">Ładowanie…</div>;

  const q = query.trim().toLowerCase();
  const matched = q
    ? orders.filter((o) => o.number.toLowerCase().includes(q) || (o.customer?.name || "").toLowerCase().includes(q) || (o.customer?.short_name || "").toLowerCase().includes(q))
    : orders;
  const todo = matched.filter((o) => QUEUE_STATUSES.includes(o.status) && !o.all_done)
    .sort((a, b) => (a.sent_to_production_at || a.created_at || "").localeCompare(b.sent_to_production_at || b.created_at || ""));
  const done = matched.filter((o) => !QUEUE_STATUSES.includes(o.status) || o.all_done);
  const dkey = (o) => (o.completed_at || o.created_at || "").slice(0, 10);
  const doneFiltered = done.filter((o) =>
    (doneStatus === "all" || o.status === doneStatus) &&
    (!dFrom || dkey(o) >= dFrom) &&
    (!dTo || dkey(o) <= dTo));

  const renderList = (list, emptyText) => list.length
    ? <div className="space-y-5">{list.map((o) => (
        <OrderCard key={o.id} o={o} onSize={onSize} onAllRequest={(x) => setConfirm({ order: x, kind: "all" })}
          onUncheckAll={(x) => onAll(x, false)} onStatus={onStatus} onNotifyClient={onNotifyClient} onZoom={(url, name) => setZoom({ url, name })}
          confirmSize={(order, index, size) => setConfirm({ order, index, size, kind: "size" })} canStatus={canStatus} pdfBase={pdfBase} vizMap={vizMap} isAdmin={isAdmin} onSentDate={onSentDate} isHaft={base === "/haft"} onHaftProject={onHaftProject} />
      ))}</div>
    : <div className="rounded-2xl border border-dashed border-border p-12 text-center font-semibold text-muted-foreground">{emptyText}</div>;

  return (
    <div data-testid="production-view">
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="prod-search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Szukaj po numerze zamówienia lub kliencie…" className="pl-9" />
      </div>
      {isAdmin && (
        <div className="mb-4 flex justify-end">
          <Button data-testid="prod-backfill-sentdate" variant="outline" size="sm" disabled={backfillBusy} onClick={onBackfill} className="gap-2 font-semibold">
            {backfillBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Uzupełnij daty wysłania (starsze zam.)
          </Button>
        </div>
      )}
      <Tabs defaultValue="todo">
        <TabsList data-testid="prod-tabs">
          <TabsTrigger value="todo" data-testid="prod-tab-todo">Do zrobienia ({todo.length})</TabsTrigger>
          <TabsTrigger value="done" data-testid="prod-tab-done">Zrealizowane ({done.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="todo" className="mt-5" data-testid="prod-panel-todo">{renderList(todo, emptyText)}</TabsContent>
        <TabsContent value="done" className="mt-5 space-y-4" data-testid="prod-panel-done">
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card px-4 py-3" data-testid="prod-done-filters">
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Status</span>
              <Select value={doneStatus} onValueChange={setDoneStatus}>
                <SelectTrigger data-testid="prod-done-status" className="w-[210px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie statusy</SelectItem>
                  {DONE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Data od</span>
              <Input data-testid="prod-done-from" type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} className="w-[160px] h-9" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">do</span>
              <Input data-testid="prod-done-to" type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} className="w-[160px] h-9" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Skróty</span>
              <div className="flex gap-1.5">
                {(() => { const p = datePresets(); const M = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"]; const nowD = new Date(); const f = (s) => s.split("-").reverse().join("."); return (<>
                  <Button data-testid="prod-preset-month" variant="outline" size="sm" className="h-auto py-1 flex-col leading-tight font-semibold" onClick={() => { setDFrom(p.month.from); setDTo(p.month.to); }}><span>Bieżący miesiąc</span><span className="text-[10px] font-normal opacity-70">{M[nowD.getMonth()]}</span></Button>
                  <Button data-testid="prod-preset-quarter" variant="outline" size="sm" className="h-auto py-1 flex-col leading-tight font-semibold" onClick={() => { setDFrom(p.quarter.from); setDTo(p.quarter.to); }}><span>Bieżący kwartał</span><span className="text-[10px] font-normal opacity-70">{f(p.quarter.from)}–{f(p.quarter.to)}</span></Button>
                  <Button data-testid="prod-preset-year" variant="outline" size="sm" className="h-auto py-1 flex-col leading-tight font-semibold" onClick={() => { setDFrom(p.year.from); setDTo(p.year.to); }}><span>Bieżący rok</span><span className="text-[10px] font-normal opacity-70">{nowD.getFullYear()}</span></Button>
                </>); })()}
              </div>
            </div>
            {(doneStatus !== "all" || dFrom || dTo) && (
              <Button data-testid="prod-done-clear" variant="outline" size="sm" className="h-9 font-semibold" onClick={() => { setDoneStatus("all"); setDFrom(""); setDTo(""); }}>Wyczyść filtry</Button>
            )}
            <span className="ml-auto font-mono text-xs font-semibold text-muted-foreground self-center" data-testid="prod-done-count">Wyników: {doneFiltered.length}</span>
          </div>
          {renderList(doneFiltered, "Brak zrealizowanych zamówień dla wybranych filtrów.")}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="prod-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Potwierdź wykonanie</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "all"
                ? "Oznaczyć WSZYSTKIE rozmiary jako wykonane? Gdy wszystkie rozmiary będą gotowe, zamówienie przejdzie do statusu „Zrealizowane”."
                : `Oznaczyć rozmiar „${confirm?.size}” jako wykonany? Zmianę można cofnąć, odznaczając go ponownie.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="prod-confirm-cancel">Anuluj</AlertDialogCancel>
            <AlertDialogAction data-testid="prod-confirm-ok" onClick={doConfirm} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">Potwierdź</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!zoom} onOpenChange={(v) => !v && setZoom(null)}>
        <DialogContent data-testid="prod-lightbox" className="max-w-4xl p-3 bg-[#0D1117] border-border">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold truncate pr-8">{zoom?.name || "Wizualizacja"}</DialogTitle>
          </DialogHeader>
          {zoom && (
            <div className="flex items-center justify-center max-h-[75vh] overflow-auto rounded-lg bg-black/40">
              <img src={zoom.url} alt={zoom.name} data-testid="prod-lightbox-img" className="max-w-full max-h-[72vh] object-contain" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
